import React, { useState, useEffect, useMemo } from 'react';
import { Clock } from 'lucide-react';
import { useQuests } from '@/features/learning/hooks/useQuests';
import ChestIcon from '@/features/learning/components/ChestIcon';

const toPersianDigits = (s: string) => s.replace(/[0-9]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[parseInt(d, 10)]);
const toPersianNumber = (n: number | string) => toPersianDigits(String(n));

// Module-level variable to track animation state across navigations (resets on reload)
let hasShownAnimation = false;

const DashboardQuests: React.FC = () => {
  const [timeRemaining, setTimeRemaining] = useState(24 * 60);
  const { quests: dailyQuests, isLoading, timeRemainingMinutes } = useQuests();

  // Capture the animation state for this render cycle
  const [shouldAnimate] = useState(!hasShownAnimation);

  useEffect(() => {
    // Mark as shown for future visits in this session
    hasShownAnimation = true;
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeRemaining(prev => Math.max(0, prev - 1));
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (typeof timeRemainingMinutes === 'number') {
      setTimeRemaining(timeRemainingMinutes);
    }
  }, [timeRemainingMinutes]);

  const targets = useMemo(
    () => dailyQuests.map(q => Math.min(100, Math.max(0, (q.target > 0 ? (q.progress / q.target) * 100 : 0))))
    , [dailyQuests]
  );
  const [widths, setWidths] = useState<number[]>([]);

  useEffect(() => {
    let cancelled = false;

    const animateIndex = async (idx: number): Promise<void> => {
      if (cancelled || idx >= dailyQuests.length) return;
      const target = targets[idx] || 0;
      const durationMs = 625;
      const easeInOut = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
      const start = performance.now();

      await new Promise<void>((resolve) => {
        const step = (now: number) => {
          if (cancelled) return resolve();
          const elapsed = now - start;
          const t = Math.min(1, elapsed / durationMs);
          const eased = easeInOut(t);
          const next = target * eased;
          setWidths(prev => {
            const base = prev.length === dailyQuests.length ? prev : new Array(dailyQuests.length).fill(0);
            const copy = [...base];
            copy[idx] = next;
            return copy;
          });
          if (t < 1) {
            requestAnimationFrame(step);
          } else {
            resolve();
          }
        };
        requestAnimationFrame(step);
      });

      await animateIndex(idx + 1);
    };

    if (dailyQuests.length > 0) {
      if (shouldAnimate) {
        setWidths(new Array(dailyQuests.length).fill(0));
        animateIndex(0);
      } else {
        // Skip animation and set directly to targets
        setWidths(targets);
      }
    }
    return () => { cancelled = true; };
  }, [dailyQuests.length, targets, shouldAnimate]);

  const formatTime = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    const txt = hours > 0 ? `${hours} ساعت` : `${mins} دقیقه`;
    return toPersianDigits(txt);
  };

  // Don't render anything if loading or no quests
  if (isLoading) {
    return (
      <div dir="rtl">
        {/* Header Shimmer */}
        <div className="relative mb-4 sm:mb-5 h-7">
          <div className="absolute right-0 top-1/2 -translate-y-1/2 pr-2">
            <div className="h-5 w-28 rounded bg-gray-200 animate-pulse" />
          </div>
          <div className="absolute left-0 top-1/2 -translate-y-1/2">
            <div className="h-4 w-20 rounded bg-gray-200 animate-pulse" />
          </div>
        </div>
        {/* Card Shimmer */}
        <div className="bg-white dark:bg-dark-bg-secondary rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-dark-border">
          <div className="space-y-4 animate-pulse">
            {[1, 2, 3].map((i) => (
              <div key={i}>
                <div className="h-4 w-32 rounded bg-gray-200 mb-2" />
                <div className="flex items-center">
                  <div className="flex-1 ml-2 h-6 rounded-r-full bg-gray-200" />
                  <div className="w-12 h-12 rounded-2xl bg-gray-200" />
                </div>
                {i < 3 && <div className="border-t border-gray-200 dark:border-dark-border mt-4 -mx-6" />}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Don't render anything if no quests
  if (dailyQuests.length === 0) {
    return null;
  }

  return (
    <div dir="rtl">
      {/* Header: title on right, time anchored left */}
      <div className="relative mb-4 sm:mb-5 h-7">
        <h2 className="absolute right-0 top-1/2 -translate-y-1/2 font-persian font-bold text-base sm:text-lg text-right text-gray-800 pr-2">چالش‌های روزانه</h2>
        {dailyQuests.length > 0 && (
          <div className="absolute left-0 top-1/2 -translate-y-1/2 flex items-center gap-1" style={{ color: 'rgb(247, 150, 46)' }}>
            <span className="font-bold text-sm">{formatTime(timeRemaining)}</span>
            <Clock className="w-4 h-4" />
          </div>
        )}
      </div>

      {/* Quests Card */}
      <div
        className="relative mx-3 overflow-visible rounded-3xl px-3 py-6 sm:px-4 ring-1 ring-black/10 bg-[#FFFDFC]"
        style={{ boxShadow: '0 0 6px 1px rgba(0, 0, 0, 0.16)' }}
      >
        <div className="space-y-[14px]">
          {dailyQuests.slice(0, 3).map((quest, index) => {
            const percent = Math.round(widths[index] || 0);
            const displayProgress = Math.min(quest.progress, quest.target);
            return (
              <div key={quest.id}>
                <div className="flex items-center gap-2">
                  <div className="min-w-0 flex-1">
                    <h2 className="text-gray-800 dark:text-dark-foreground text-[13px] font-bold mb-1 font-persian">{toPersianDigits(quest.title)}</h2>
                    <div className="relative h-5 w-[calc(100%-4px)] overflow-hidden rounded-full bg-gray-200 shadow-inner">
                      <div className="absolute right-0 top-0 bottom-0 rounded-full" style={{ width: `${percent}%`, transition: 'none', backgroundColor: '#FBBF24' }} />
                      <div className="absolute inset-0 z-10 grid place-items-center pointer-events-none">
                        <span className={`text-[10px] font-semibold leading-none ${percent >= 50 ? 'text-white' : 'text-gray-800 dark:text-dark-foreground'}`}>
                          {toPersianNumber(displayProgress)} / {toPersianNumber(quest.target)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex h-[52px] w-[52px] shrink-0 translate-y-[2px] items-center justify-center">
                    <div className="h-11 w-11">
                      <ChestIcon />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default DashboardQuests;
